/**
 * A clip filmed on a green screen, drawn with the green keyed out (chroma-key.ts), so the avatar stands on any
 * background (stage/catalog.ts). `still`: the first frame only (animations off). Without WebGL nothing is drawn, and
 * it is said once.
 */
import { useEffect, useRef, type CSSProperties } from 'react';
import { reportOnce } from '../core/diagnostics';
import type { ChromaKey } from '../stage/catalog';
import { keyer } from './chroma-key';

export const FILL: CSSProperties = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' };

export function KeyedVideo({ video, poster, keying, still = false, style }: {
  video: string;
  poster: string;
  keying: ChromaKey;
  still?: boolean;
  style?: CSSProperties;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const gl = canvas?.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false });
    if (!gl) {
      reportOnce('warn', 'avatar', 'no WebGL: the avatar cannot be keyed out of its green screen, it is not shown');
      return;
    }
    let draw: ReturnType<typeof keyer>;
    try {
      draw = keyer(gl, keying);
    } catch (e) {
      reportOnce('error', 'avatar', 'the chroma key does not compile', e);
      return;
    }
    let stopped = false;
    const image = new Image();
    image.onload = () => !stopped && draw(image, image.naturalWidth, image.naturalHeight);
    image.src = poster;
    if (still) return () => { stopped = true; };

    const clip = document.createElement('video');
    Object.assign(clip, { src: video, muted: true, loop: true, playsInline: true, autoplay: true, crossOrigin: 'anonymous' });
    type FrameCallback = (cb: () => void) => number;
    const onFrame = (clip as HTMLVideoElement & { requestVideoFrameCallback?: FrameCallback }).requestVideoFrameCallback?.bind(clip);
    const tick = () => {
      if (stopped) return;
      if (clip.readyState >= 2) draw(clip, clip.videoWidth, clip.videoHeight);
      if (onFrame) onFrame(tick);
      else requestAnimationFrame(tick);
    };
    // an AbortError is this effect's own cleanup pausing it (a remount): not a problem
    void clip.play().catch((e: unknown) => (e as Error).name !== 'AbortError' && reportOnce('warn', 'avatar', 'the avatar idle does not play', e));
    tick();
    return () => {
      stopped = true;
      clip.pause();
      clip.removeAttribute('src');
      clip.load();
    };
  }, [video, poster, keying, still]);

  return <canvas ref={canvasRef} style={{ ...FILL, ...style }} data-face="idle" aria-hidden />;
}
